/**
 * Etapa 1 do autocadastro — contatos da inscrição e os veículos do SGTP.
 *
 * **Contatos (RN-CP-21).** Vêm preenchidos com o que o SGTP tem da entidade e
 * são editáveis: o e-mail informado aqui é o canal oficial de comunicação da
 * inscrição (edital, item 4.1).
 *
 * **Veículos (RN-CP-22).** A lista é a frota da entidade no SGTP, veículo a
 * veículo (contrato de F02). Só o de licença regular, ainda não inscrito neste
 * período, pode ser marcado; os outros aparecem com o motivo, sem caixa de
 * seleção — o que não se pode fazer não é renderizado (regra 7 de interface).
 * A caixa vem primeiro na linha (regra 10).
 */

import { AlertCircle, Car, Mail, Phone } from 'lucide-react';
import { useId } from 'react';

import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Checkbox } from '@/ui/base/checkbox';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { cn } from '@/nucleo/utilitarios';

import {
  mensagemDoErro,
  type ContatosDoRascunho,
  type MotivoDeVeiculoIndisponivel,
  type VeiculoDoPerfil,
} from './apiDaInscricao';
import { formatarPlaca, mascaraTelefone, somenteDigitos } from './contrato';

const MOTIVO: Record<MotivoDeVeiculoIndisponivel, string> = {
  LICENCA_IRREGULAR: 'Licença irregular no SGTP',
  LICENCA_VENCIDA: 'Licença vencida no SGTP',
  JA_INSCRITO_NO_CICLO: 'Já inscrito neste período',
};

/**
 * `AAAA-MM-DD` → `DD/MM/AAAA` sem passar por `Date`: data civil lida como
 * meia-noite UTC voltaria um dia no fuso de São Paulo.
 */
function dataCivil(valor: string): string {
  const [ano, mes, dia] = valor.split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
}

const ROTULO_DO_TIPO: Record<string, string> = {
  VAN: 'Van',
  MICRO_ONIBUS: 'Micro-ônibus',
  ONIBUS: 'Ônibus',
};

export interface EtapaVeiculosSgtpProps {
  contatos: ContatosDoRascunho;
  aoTrocarContatos: (contatos: ContatosDoRascunho) => void;
  veiculos: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    data: VeiculoDoPerfil[] | undefined;
    refetch: () => unknown;
  };
  placasMarcadas: string[];
  aoAlternar: (placa: string, marcado: boolean) => void;
}

export function EtapaVeiculosSgtp({
  contatos,
  aoTrocarContatos,
  veiculos,
  placasMarcadas,
  aoAlternar,
}: EtapaVeiculosSgtpProps) {
  const idBase = useId();
  const marcadas = new Set(placasMarcadas);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contatos da inscrição</CardTitle>
          <CardDescription>
            Vieram do SGTP; ajuste se precisar. O e-mail é o canal oficial: é por ele que a SMT
            avisa cada passo da inscrição.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idBase}-email`} className="flex items-center gap-1.5">
              <Mail aria-hidden="true" className="h-3.5 w-3.5" />
              E-mail
            </Label>
            <Input
              id={`${idBase}-email`}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={contatos.email ?? ''}
              onChange={(e) => aoTrocarContatos({ ...contatos, email: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idBase}-telefone`} className="flex items-center gap-1.5">
              <Phone aria-hidden="true" className="h-3.5 w-3.5" />
              Telefone <span className="text-muted-foreground">(opcional)</span>
            </Label>
            <Input
              id={`${idBase}-telefone`}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(11) 90000-0000"
              maxLength={15}
              // Mostra com máscara, guarda só dígitos: o que vai à API e à
              // validação continua `11900000102`.
              value={mascaraTelefone(contatos.telefone)}
              onChange={(e) =>
                aoTrocarContatos({ ...contatos, telefone: somenteDigitos(e.target.value) })
              }
            />
          </div>
        </CardContent>
      </Card>

      <section aria-labelledby={`${idBase}-frota`} className="space-y-3">
        <div className="space-y-1">
          <h3 id={`${idBase}-frota`} className="text-base font-semibold text-foreground">
            Veículos no SGTP
          </h3>
          <p className="text-sm text-muted-foreground">
            Marque os veículos que vão entrar nesta inscrição. Cada um ganha o seu protocolo.
          </p>
        </div>

        {veiculos.isPending && <Spinner rotulo="Consultando a frota no SGTP" />}

        {veiculos.isError && (
          <ErrorState
            variant="load-failed"
            description={mensagemDoErro(
              veiculos.error,
              'Não foi possível consultar a frota no SGTP agora.',
            )}
            primaryAction={{ label: 'Tentar de novo', onClick: () => void veiculos.refetch() }}
          />
        )}

        {veiculos.data && veiculos.data.length === 0 && (
          <EmptyState
            icon={Car}
            title="Nenhum veículo nesta entidade no SGTP"
            description="Sem veículo cadastrado não há o que inscrever. Procure o DTP para atualizar o cadastro no SGTP."
          />
        )}

        {veiculos.data && veiculos.data.length > 0 && (
          <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
            {veiculos.data.map((veiculo) => {
              const id = `${idBase}-veiculo-${veiculo.placa}`;
              const marcado = marcadas.has(veiculo.placa);
              const detalhe = (
                <span className="min-w-0 flex-1">
                  <span className="block font-mono font-medium text-foreground">
                    {formatarPlaca(veiculo.placa)}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {ROTULO_DO_TIPO[veiculo.tipo] ?? veiculo.tipo} · {veiculo.modelo} (
                    {veiculo.anoFabricacao}) · {veiculo.capacidade} lugares
                    {veiculo.acessivel ? ' · acessível' : ''}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Licença {veiculo.numeroLicenca} · válida até{' '}
                    {dataCivil(veiculo.validadeLicenca)}
                  </span>
                </span>
              );

              if (!veiculo.selecionavel) {
                return (
                  <li key={veiculo.placa} className="flex items-start gap-3 p-3 text-sm">
                    <AlertCircle
                      aria-hidden="true"
                      className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                    />
                    {detalhe}
                    <Badge variant="outline" className="shrink-0 border-warning/40">
                      {veiculo.motivo ? MOTIVO[veiculo.motivo] : 'Indisponível'}
                    </Badge>
                  </li>
                );
              }

              return (
                <li key={veiculo.placa}>
                  <Label
                    htmlFor={id}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 p-3 text-sm font-normal',
                      marcado ? 'bg-primary/5' : 'hover:bg-muted/40',
                    )}
                  >
                    <Checkbox
                      id={id}
                      checked={marcado}
                      className="mt-0.5"
                      onCheckedChange={(valor) => aoAlternar(veiculo.placa, valor === true)}
                    />
                    {detalhe}
                  </Label>
                </li>
              );
            })}
          </ul>
        )}

        {placasMarcadas.length > 0 && (
          <Alert>
            <Car aria-hidden="true" className="h-4 w-4" />
            <AlertDescription>
              {placasMarcadas.length} veículo(s) marcado(s) para esta inscrição.
            </AlertDescription>
          </Alert>
        )}
      </section>
    </div>
  );
}

export default EtapaVeiculosSgtp;
